/**
 * Background data
 * 50+ solid backgrounds (colors, wall textures)
 * Used in gallery wall mode
 */

export interface BackgroundData {
  id: string;
  name: string;
  color: string;
  category: BackgroundCategory;
  /** CSS background for texture (gradient, pattern, etc) */
  cssBackground?: string;
  isPremium?: boolean;
}

export type BackgroundCategory = 'white' | 'neutral' | 'warm' | 'cool' | 'dark' | 'texture';

/** Background categories */
export const BACKGROUND_CATEGORIES: { id: BackgroundCategory | 'all'; label: string }[] = [
  { id: 'all', label: 'All' },
  { id: 'white', label: 'White' },
  { id: 'neutral', label: 'Neutral' },
  { id: 'warm', label: 'Warm' },
  { id: 'cool', label: 'Cool' },
  { id: 'dark', label: 'Dark' },
  { id: 'texture', label: 'Texture' },
];

/** All background definitions */
export const BACKGROUNDS: BackgroundData[] = [
  // === White ===
  { id: 'bg-white', name: 'Pure White', color: '#ffffff', category: 'white' },
  { id: 'bg-snow', name: 'Snow', color: '#fffafa', category: 'white' },
  { id: 'bg-ivory', name: 'Ivory', color: '#fffff0', category: 'white' },
  { id: 'bg-seashell', name: 'Seashell', color: '#fff5ee', category: 'white' },
  { id: 'bg-linen', name: 'Linen White', color: '#faf0e6', category: 'white' },
  { id: 'bg-cloud', name: 'Cloud', color: '#f8f8ff', category: 'white' },

  // === Neutral ===
  { id: 'bg-light-grey', name: 'Light Grey', color: '#e0e0e0', category: 'neutral' },
  { id: 'bg-silver', name: 'Silver', color: '#c0c0c0', category: 'neutral' },
  { id: 'bg-grey', name: 'Grey', color: '#9e9e9e', category: 'neutral' },
  { id: 'bg-cream', name: 'Cream', color: '#f5f5dc', category: 'neutral' },
  { id: 'bg-greige', name: 'Greige', color: '#cec7bc', category: 'neutral' },
  { id: 'bg-mushroom', name: 'Mushroom', color: '#c9b9a8', category: 'neutral' },
  { id: 'bg-stone', name: 'Stone', color: '#b8b0a0', category: 'neutral' },
  { id: 'bg-platinum', name: 'Platinum', color: '#d9d5d0', category: 'neutral' },

  // === Warm ===
  { id: 'bg-sand', name: 'Sand', color: '#e8d4b0', category: 'warm' },
  { id: 'bg-peach', name: 'Peach', color: '#ffdab9', category: 'warm' },
  { id: 'bg-blush', name: 'Blush', color: '#f5d0c5', category: 'warm' },
  { id: 'bg-terracotta', name: 'Terracotta', color: '#cc7755', category: 'warm', isPremium: true },
  { id: 'bg-sienna', name: 'Sienna', color: '#a0522d', category: 'warm', isPremium: true },
  { id: 'bg-honey', name: 'Honey', color: '#e8b060', category: 'warm' },
  { id: 'bg-copper', name: 'Copper', color: '#b87333', category: 'warm', isPremium: true },
  { id: 'bg-camel', name: 'Camel', color: '#c19a6b', category: 'warm' },
  { id: 'bg-apricot', name: 'Apricot', color: '#fbceb1', category: 'warm' },

  // === Cool ===
  { id: 'bg-ice-blue', name: 'Ice Blue', color: '#e0eef8', category: 'cool' },
  { id: 'bg-powder-blue', name: 'Powder Blue', color: '#b0d4e8', category: 'cool' },
  { id: 'bg-sage', name: 'Sage', color: '#b2c2a0', category: 'cool' },
  { id: 'bg-mint', name: 'Mint', color: '#c0e8d0', category: 'cool' },
  { id: 'bg-slate', name: 'Slate', color: '#708090', category: 'cool' },
  { id: 'bg-steel', name: 'Steel Blue', color: '#4682b4', category: 'cool', isPremium: true },
  { id: 'bg-ocean', name: 'Ocean', color: '#4a8fa0', category: 'cool', isPremium: true },
  { id: 'bg-lavender', name: 'Lavender', color: '#d8d0e8', category: 'cool' },
  { id: 'bg-dusty-blue', name: 'Dusty Blue', color: '#8ca0b0', category: 'cool' },

  // === Dark ===
  { id: 'bg-charcoal', name: 'Charcoal', color: '#424242', category: 'dark' },
  { id: 'bg-anthracite', name: 'Anthracite', color: '#383838', category: 'dark' },
  { id: 'bg-navy', name: 'Navy', color: '#1a237e', category: 'dark', isPremium: true },
  { id: 'bg-forest', name: 'Forest', color: '#1b5e20', category: 'dark', isPremium: true },
  { id: 'bg-burgundy', name: 'Burgundy', color: '#800020', category: 'dark', isPremium: true },
  { id: 'bg-noir', name: 'Noir', color: '#1a1a1a', category: 'dark' },
  { id: 'bg-midnight', name: 'Midnight', color: '#191970', category: 'dark', isPremium: true },
  { id: 'bg-espresso', name: 'Espresso', color: '#3c2415', category: 'dark' },

  // === Texture (CSS-based) ===
  {
    id: 'bg-concrete',
    name: 'Concrete',
    color: '#a0a0a0',
    category: 'texture',
    cssBackground:
      'linear-gradient(135deg, #a0a0a0 0%, #989898 25%, #a8a8a8 50%, #959595 75%, #a0a0a0 100%)',
  },
  {
    id: 'bg-brick',
    name: 'Brick',
    color: '#8b5a2b',
    category: 'texture',
    cssBackground: 'linear-gradient(0deg, #7a4a22 0%, #8b5a2b 50%, #9a6a3b 100%)',
    isPremium: true,
  },
  {
    id: 'bg-marble',
    name: 'Marble',
    color: '#f0ece8',
    category: 'texture',
    cssBackground: 'linear-gradient(135deg, #f0ece8 0%, #e8e0d8 30%, #f5f0eb 60%, #e0d8d0 100%)',
    isPremium: true,
  },
  {
    id: 'bg-plaster',
    name: 'Plaster',
    color: '#f0e8d8',
    category: 'texture',
    cssBackground: 'linear-gradient(180deg, #f0e8d8 0%, #ece0d0 50%, #f2eae0 100%)',
  },
  {
    id: 'bg-wood-panel',
    name: 'Wood Panel',
    color: '#a08060',
    category: 'texture',
    cssBackground: 'linear-gradient(0deg, #906e50 0%, #a08060 30%, #b09070 60%, #a08060 100%)',
    isPremium: true,
  },
  {
    id: 'bg-stucco',
    name: 'Stucco',
    color: '#e8e0d0',
    category: 'texture',
    cssBackground: 'linear-gradient(145deg, #e8e0d0 0%, #e0d8c8 35%, #ece4d8 70%, #e0d8c8 100%)',
  },
];

/** Get background by ID */
export function getBackground(bgId: string): BackgroundData | undefined {
  return BACKGROUNDS.find((b) => b.id === bgId);
}

/** Gallery wall backgrounds - includes both bg-* IDs and legacy simple keys */
export const GALLERY_BACKGROUNDS: Record<string, string> = {
  // Legacy simple keys (used by GalleryWallEditor)
  white: '#ffffff',
  cream: '#f5f5dc',
  'light-grey': '#e0e0e0',
  grey: '#9e9e9e',
  charcoal: '#424242',
  navy: '#1a237e',
  forest: '#1b5e20',
  burgundy: '#800020',
  // New bg-* IDs
  ...Object.fromEntries(BACKGROUNDS.map((b) => [b.id, b.color])),
};
